import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Login from './pages/Login';
import Landing from './pages/Landing';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import CreateQR from './pages/CreateQR';
import QRDetail from './pages/QRDetail';
import RedirectPage from './pages/RedirectPage';
import Admin from './pages/Admin';
import Giveaway from './pages/Giveaway';
import GiveawayCodeManager from './pages/GiveawayCodeManager';
import { Toaster } from 'react-hot-toast';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/session`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        
        // Set Sentry user context
        if (window.Sentry) {
          window.Sentry.setUser({
            id: data.user.id,
            email: data.user.email,
            username: data.user.name
          });
        }
      } else {
        // Token invalid or expired - clear it
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Session check failed:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Clear token from localStorage
      localStorage.removeItem('token');
      setUser(null);
      
      // Clear Sentry user context
      if (window.Sentry) {
        window.Sentry.setUser(null);
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Clear token anyway
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={user ? <Navigate to="/projects" /> : <Login onLogin={setUser} />} />
        <Route path="/projects" element={user ? <Projects user={user} onLogout={logout} /> : <Navigate to="/login" />} />
        <Route path="/projects/:projectId" element={user ? <ProjectDetail user={user} onLogout={logout} /> : <Navigate to="/login" />} />
        <Route path="/projects/:projectId/create-qr" element={user ? <CreateQR user={user} onLogout={logout} /> : <Navigate to="/login" />} />
        <Route path="/qr/:qrId" element={user ? <QRDetail user={user} onLogout={logout} /> : <Navigate to="/login" />} />
        <Route path="/admin" element={user ? <Admin user={user} onLogout={logout} /> : <Navigate to="/login" />} />
        <Route path="/giveaway" element={user ? <Giveaway user={user} onLogout={logout} /> : <Navigate to="/login" />} />
        <Route path="/giveaway/:campaignId" element={user ? <GiveawayCodeManager user={user} onLogout={logout} /> : <Navigate to="/login" />} />
        <Route path="/r/:shortCode" element={<RedirectPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
